import React, { useEffect, useMemo } from 'react'

import {
  AnimatedGradientEffect,
  AuroraEffect,
  BeamsEffect,
  BoxesEffect,
  DotMatrixEffect,
  FlickerGridEffect,
  FloatingBubblesEffect,
  FluidGradientEffect,
  GradientEffect,
  MatrixRainEffect,
  MeshGradientEffect,
  MeteorsEffect,
  NeuralNetworkEffect,
  NoiseEffect,
  OrbsEffect,
  ParticlesFlowEffect,
  PathsEffect,
  RippleWavesEffect,
  ShaderCloudsEffect,
  SparklesEffect,
  SpotlightEffect,
  StarsEffect,
  WavyEffect,
} from '../effects'
import { EffectBackgroundProps } from '../types'
import { injectEffectKeyframes } from '../utils/effectKeyframes'

/**
 * EffectBackground - Coordinator component that renders the appropriate effect
 */
export const EffectBackground = React.memo(({ layerStyle, index, values }: EffectBackgroundProps) => {
  const effectType = values.effectType || 'none'

  const resolveColor = (color: string | undefined) => {
    if (!color) return '#000000'
    if (color.startsWith('#') || color.startsWith('rgb') || color.startsWith('hsl')) {
      return color
    }
    return `var(--color-${color})`
  }

  const resolvedValues = useMemo(() => {
    return {
      ...values,
      effectBackgroundColor: resolveColor(values.effectBackgroundColor),
      effectColors: (values.effectColors || []).map((c: string) => resolveColor(c)),
    }
  }, [values])

  useEffect(() => {
    injectEffectKeyframes()
  }, [])

  if (effectType === 'none') {
    return null
  }

  switch (effectType) {
    case 'gradient':
      return <GradientEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'animated-gradient':
      return <AnimatedGradientEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'beams':
      return <BeamsEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'wavy':
      return <WavyEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'sparkles':
      return <SparklesEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'stars':
      return <StarsEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'noise':
      return <NoiseEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'mesh-gradient':
      return <MeshGradientEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'aurora':
      return <AuroraEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'flicker-grid':
      return <FlickerGridEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'meteors':
      return <MeteorsEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'boxes':
      return <BoxesEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'paths':
      return <PathsEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'fluid-gradient':
      return <FluidGradientEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'shader-clouds':
      return <ShaderCloudsEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'orbs':
      return <OrbsEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'dot-matrix':
      return <DotMatrixEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'spotlight':
      return <SpotlightEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'ripple-waves':
      return <RippleWavesEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'floating-bubbles':
      return <FloatingBubblesEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'neural-network':
      return <NeuralNetworkEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'matrix-rain':
      return <MatrixRainEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    case 'particles-flow':
      return <ParticlesFlowEffect layerStyle={layerStyle} index={index} values={resolvedValues} />

    default:
      return null
  }
})
EffectBackground.displayName = 'EffectBackground'
